import classNames from "classnames";

import { LISCase } from "pwapi";

import { pieceCountEnumToLabel } from "shared/utils/blockUtils";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import { useAppSelector } from "app/hooks";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import CasePriority from "ui/components/CasePriority";
import SpecimenProtocolBanner from "ui/components/SpecimenProtocolBanner";
import { labelForBlock } from "util/blockUtils";
import { blockSort, specimenSort } from "util/sortFunctions";

const ScannedBlockModal = ({
  scanned,
}: {
  scanned: {
    block: LISCase["blocks"][0];
    specimen?: LISCase["specimens"][0];
    case: LISCase;
  };
}) => {
  const lab = useSelectedLab();
  const clinic = useAppSelector((state) =>
    scanned.case?.patient?.clinic_id
      ? state.clinics[scanned.case.patient.clinic_id]
      : undefined
  );
  return (
    <article
      className={classNames("ScannedCase--large", {
        "ScannedCase--blue": (scanned?.specimen?.protocols?.length ?? 0) > 0,
      })}
    >
      <aside className="ScannedCase--large__aside">
        <small className="ScannedCase--large__CaseInfo">
          {scanned.case.priority && (
            <CasePriority priorityCase={scanned.case} />
          )}
          <code>
            <span
              key={scanned.case.accession}
              className="ScannedCase--large__value"
            >
              <AccessionNumber accession={scanned.case.accession} />
            </span>
          </code>
          {lab.settings?.new_york_lis_tag &&
            clinic?.location?.state === "NY" && (
              <div className="Tag Tag--purple">New York</div>
            )}
        </small>
        <div>
          <div className="ScannedCase--large__blocks">
            <strong>
              {scanned.case.blocks.length === 1
                ? "1 Block Total: "
                : `${scanned.case.blocks.length} Blocks Total: `}
              &nbsp;
            </strong>
            {scanned.case.specimens.sort(specimenSort).map((s) =>
              scanned.case.blocks
                .filter((b) => b.specimen_id === s.id)
                .sort(blockSort)
                .map((block) => (
                  <span key={block.id}>
                    <code>{labelForBlock(block, scanned.case)}</code>
                  </span>
                ))
            )}
          </div>
          {scanned.block.piece_count && (
            <div className="ScannedCase--large__pieceCount">
              <strong>Piece Count: </strong>
              <code
                key={scanned.block.piece_count}
                className="ScannedCase--large__value"
              >
                {pieceCountEnumToLabel(scanned.block.piece_count)}
              </code>
            </div>
          )}
        </div>
      </aside>
      <header className="ScannedCase--large__header">
        <h2 className="ScannedCase--large__BlockLabel">
          <span key={scanned.block.id} className="ScannedCase--large__value">
            {labelForBlock(scanned.block, scanned.case)}
          </span>
        </h2>{" "}
        <p className="ScannedCase--large__procedure">
          <code
            key={scanned.specimen?.procedure}
            className="ScannedCase--large__procedureName ScannedCase--large__value"
          >
            {procedureEnumToLabel(scanned.specimen?.procedure)}
          </code>
          <code
            key={scanned.specimen?.site}
            className="ScannedCase--large__site ScannedCase--large__value"
          >
            {scanned.specimen?.site}
          </code>
        </p>
      </header>
      <div className="ScannedCase--large__protocols">
        {scanned.specimen && (
          <SpecimenProtocolBanner
            key={scanned.specimen.id}
            specimen={scanned.specimen}
            specimens={scanned.case.specimens}
          />
        )}
      </div>

      <hr />
      <h3>Gross Description</h3>
      <p>
        <span key={scanned.specimen?.id} className="ScannedCase--large__value">
          {scanned.specimen?.gross_description}
        </span>
      </p>
    </article>
  );
};

export default ScannedBlockModal;
